import type { RefusalReason } from "./types";

export const NEXA_NAME = "Nexa AI";

export const NEXA_COLORS = {
  indigo: "#4F46E5",
  deep: "#312E81",
  violet: "#6C5BFF",
  lime: "#A3E635",
  tint: "#EEF0FE",
  surface: "#1E1B4B",
} as const;

export type NexaLayout = "floating" | "sidebar" | "fullscreen";

export const CHAT_WS_SUBPROTOCOL = "chattrade";

export const GTC_HEADER_HEIGHT = 48;

export const GTC_NAV_WIDTH_OPEN = 260;
export const GTC_NAV_WIDTH_COLLAPSED = 60;

export const NEXA_FLOATING_WIDTH = 420;

export const NEXA_SIDEBAR_DEFAULT_WIDTH = 440;
export const NEXA_SIDEBAR_MIN_WIDTH = 340;
export const NEXA_SIDEBAR_MAX_WIDTH = 760;

export const NEXA_SIDEBAR_MIN_VIEWPORT = 1100;

export const NEXA_RECONNECT_BASE_DELAY_MS = 1000;
export const NEXA_RECONNECT_MAX_DELAY_MS = 15000;
export const NEXA_MAX_RECONNECT_ATTEMPTS = 5;
export const NEXA_RECONNECT_STABLE_MS = 10000;

export const NEXA_TURN_SLOW_MS = 30000;

export interface RefusalCopy {
  title: string;
  action: string;
  canRetry: boolean;
  suggestRephrase: boolean;
  tone: "neutral" | "warning";
}

export const REFUSAL_COPY: Record<RefusalReason, RefusalCopy> = {
  source_unavailable: {
    title: "A data source didn't respond",
    action: "This isn't a problem with your question. Try again in a moment.",
    canRetry: true,
    suggestRephrase: false,
    tone: "warning",
  },
  unverified_claim: {
    title: "I couldn't source that answer",
    action: "I had no verifiable source for part of the answer, so I've withheld it. Rephrasing or narrowing the question usually helps.",
    canRetry: false,
    suggestRephrase: true,
    tone: "warning",
  },
  disclosure_refused: {
    title: "I can't share that",
    action: "Ask me a compliance question instead — classifications, duties, controls, origin or licensing.",
    canRetry: false,
    suggestRephrase: false,
    tone: "neutral",
  },
};

export const HS_NONE_MATCH_COPY = {
  prompt: "None of these match?",
  supplyExact: "Supply an exact HS code",
  runClassifier: "Run the HS Classifier",
  classifierNote: "Takes 30–100 seconds and uses classification credits.",
} as const;

export const HS_UNCONFIRMED_WARNING = "Generated by AI — no verified tariff match was found. Check before using.";

export const SOURCE_UNVERIFIED_WARNING = "AI-generated, not a tariff-database match";

export const NEXA_INTENTS: { label: string; prompt: string }[] = [
  { label: "Landed Cost", prompt: "I want to work out the landed cost for a shipment." },
  { label: "Tariff and Duties", prompt: "I want to check the duty and tariff rates for a product." },
  { label: "Controls", prompt: "I want to check whether a product is subject to export controls." },
  { label: "ECCN", prompt: "I want to find the ECCN for a product." },
  { label: "License", prompt: "I want to check whether an export license is required." },
  { label: "Documents", prompt: "I want to know which documents are required for a shipment." },
  { label: "Embargoes", prompt: "I want to check whether a destination is embargoed or sanctioned." },
];

export const NEXA_SUGGESTED_PROMPTS = [
  "Are there any sanctions or embargoes on exporting from Germany to Iran?",
  "What import controls apply to HS 85423100 entering India?",
  "Do I need an export licence for ECCN 3A001 from the US to Mexico?",
  "Which trade agreements could reduce the duty on HS 85423100 from Vietnam into the US?",
];

export const NEXA_WORKSPACE_INTRO =
  "Ask about duty rates and trade agreements, HS codes, import and export controls, ECCNs, export licences, sanctions, required shipment documents and landed cost.";

export const NEXA_DISCLAIMER = "Uses AI. Verify results.";

export const NEXA_UNTITLED_THREAD = "New conversation";
